import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

type OSOption = {
  id: string;
  label: string;
  status: "ready" | "discussion";
  color: string;
  features: { label: string; supported: boolean }[];
  note: string;
};

const OS_OPTIONS: OSOption[] = [
  {
    id: "yocto",
    label: "Yocto Linux",
    status: "ready",
    color: "#4ade80",
    features: [
      { label: "Cloud build pipeline", supported: true },
      { label: "Emulator hosting", supported: true },
      { label: "CI/CD integration", supported: true },
      { label: "Snapshot support", supported: true },
      { label: "GPU acceleration", supported: true },
    ],
    note: "Demo-ready today",
  },
  {
    id: "qnx",
    label: "QNX",
    status: "discussion",
    color: "#818cf8",
    features: [
      { label: "Cloud build pipeline", supported: false },
      { label: "Emulator hosting", supported: false },
      { label: "CI/CD integration", supported: true },
      { label: "Snapshot support", supported: false },
      { label: "GPU acceleration", supported: false },
    ],
    note: "Proprietary toolchain",
  },
];

export function HostOSSlide() {
  const svgW = 900;
  const svgH = 400;
  const colW = 380;
  const colGap = 60;
  const startX = (svgW - colW * 2 - colGap) / 2;
  const startY = 30;
  const featY = 130;
  const featH = 36;

  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="md">
        <SlideTitle
          title="Host OS Support"
          subtitle="Platform support for hypervisor host operating systems"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "62vh" }}
            aria-label="Host OS support comparison between Yocto Linux and QNX"
          >
            {OS_OPTIONS.map((os, colIdx) => {
              const x = startX + colIdx * (colW + colGap);
              const isReady = os.status === "ready";

              return (
                <motion.g
                  key={os.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + colIdx * 0.2, duration: 0.45 }}
                >
                  {/* Column card */}
                  <rect
                    x={x}
                    y={startY}
                    width={colW}
                    height={svgH - startY - 20}
                    rx={12}
                    fill="rgba(255,255,255,0.025)"
                    stroke={os.color}
                    strokeWidth={1.5}
                    strokeOpacity={0.2}
                  />

                  {/* Top accent bar */}
                  <rect
                    x={x}
                    y={startY}
                    width={colW}
                    height={4}
                    rx={2}
                    fill={os.color}
                    opacity={0.6}
                  />

                  {/* OS name */}
                  <text
                    x={x + 20}
                    y={startY + 40}
                    fontSize={20}
                    fill={os.color}
                    fontWeight={800}
                    fontFamily="inherit"
                  >
                    {os.label}
                  </text>

                  {/* Status badge */}
                  <rect
                    x={x + 20}
                    y={startY + 54}
                    width={isReady ? 90 : 130}
                    height={22}
                    rx={11}
                    fill={os.color}
                    opacity={0.12}
                  />
                  <text
                    x={x + 20 + (isReady ? 45 : 65)}
                    y={startY + 69}
                    textAnchor="middle"
                    fontSize={10}
                    fill={os.color}
                    fontWeight={600}
                    fontFamily="inherit"
                  >
                    {isReady ? "✓ Demo-Ready" : "Discussion Only"}
                  </text>

                  {/* Feature list */}
                  {os.features.map((feat, fi) => {
                    const fy = featY + fi * featH;
                    return (
                      <motion.g
                        key={feat.label}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7 + colIdx * 0.2 + fi * 0.06 }}
                      >
                        {/* Check/X icon */}
                        <circle
                          cx={x + 30}
                          cy={fy + 10}
                          r={8}
                          fill={
                            feat.supported
                              ? "rgba(74,222,128,0.12)"
                              : "rgba(248,113,113,0.12)"
                          }
                        />
                        <text
                          x={x + 30}
                          y={fy + 14}
                          textAnchor="middle"
                          fontSize={10}
                          fill={feat.supported ? "#4ade80" : "#f87171"}
                          fontWeight={700}
                          fontFamily="inherit"
                        >
                          {feat.supported ? "✓" : "✗"}
                        </text>
                        {/* Feature label */}
                        <text
                          x={x + 48}
                          y={fy + 14}
                          fontSize={12}
                          fill={
                            feat.supported
                              ? "rgba(255,255,255,0.8)"
                              : "rgba(255,255,255,0.35)"
                          }
                          fontFamily="inherit"
                          fontWeight={feat.supported ? 500 : 400}
                        >
                          {feat.label}
                        </text>
                      </motion.g>
                    );
                  })}

                  {/* Bottom note */}
                  <motion.text
                    x={x + colW / 2}
                    y={svgH - 45}
                    textAnchor="middle"
                    fontSize={10}
                    fill="rgba(255,255,255,0.4)"
                    fontFamily="inherit"
                    fontStyle="italic"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 + colIdx * 0.2 }}
                  >
                    {os.note}
                  </motion.text>
                </motion.g>
              );
            })}
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
